import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Trophy, Medal, TrendingUp, Target
} from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeaderboardPage() {
  const { sport, competitionId } = useParams();
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState(competitionId || 'overall');
  const [loading, setLoading] = useState(true);

  const sportColor = sport === 'AFL' ? '#E00000' : sport === 'NRL' ? '#007A33' : '#007AFF';
  const sportBadgeClass = sport === 'AFL' ? 'tipster-badge-afl' : sport === 'NRL' ? 'tipster-badge-nrl' : 'tipster-badge-combined';

  useEffect(() => {
    fetchCompetitions();
  }, [sport]);

  useEffect(() => {
    if (selectedCompetition) {
      fetchLeaderboard();
    }
  }, [selectedCompetition, sport]);

  const fetchCompetitions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/competitions?sport=${sport}`, { withCredentials: true });
      setCompetitions(response.data);
    } catch (err) {
      console.error('Error fetching competitions:', err);
    }
  };

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      let url;
      if (selectedCompetition === 'overall') {
        url = `${API_URL}/api/leaderboard/overall/${sport}`;
      } else {
        url = `${API_URL}/api/leaderboard/${selectedCompetition}`;
      }
      
      const response = await axios.get(url, { withCredentials: true });
      setLeaderboard(response.data);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setLeaderboard([]);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Medal className="w-5 h-5 text-[#FFD700]" />;
      case 2:
        return <Medal className="w-5 h-5 text-[#C0C0C0]" />;
      case 3:
        return <Medal className="w-5 h-5 text-[#CD7F32]" />;
      default:
        return <span className="text-gray-400 font-mono">{rank}</span>;
    }
  };

  const currentUserRank = leaderboard.find(e => e.user_id === user?.user_id);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span 
              className="w-4 h-4 rounded-sm"
              style={{ backgroundColor: sportColor }}
            />
            <span className={sportBadgeClass}>{sport === 'COMBINED' ? 'AFL + NRL 2026' : `${sport} 2026`}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter" data-testid="leaderboard-heading">
            Leaderboard
          </h1>
        </div>

        {/* Competition Filter */}
        <div className="mb-8">
          <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Competition</label>
          <Select value={selectedCompetition} onValueChange={setSelectedCompetition}>
            <SelectTrigger className="w-full sm:w-72 bg-white border-gray-200" data-testid="leaderboard-competition-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              {sport !== 'COMBINED' && (
                <SelectItem value="overall">Overall - All Public Competitions</SelectItem>
              )}
              {competitions.map((comp) => (
                <SelectItem key={comp.competition_id} value={comp.competition_id}>
                  {comp.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* User's Current Position */}
        {currentUserRank && (
          <div className="tipster-card mb-8 border-[#007AFF]/30">
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Your Position</div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#007AFF]/20 rounded-sm flex items-center justify-center">
                  {getRankIcon(currentUserRank.rank)}
                </div>
                <div>
                  <div className="font-bold text-lg">{currentUserRank.name}</div>
                  <div className="text-gray-500 text-sm">Rank #{currentUserRank.rank}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-black" style={{ color: sportColor }}>
                  {Number(currentUserRank.total_points || 0).toFixed(2)}
                </div>
                <div className="text-gray-500 text-sm">points</div>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard Table */}
        <div className="tipster-card p-0 overflow-hidden">
          {/* Header */}
          <div className="leaderboard-header leaderboard-row">
            <div>Rank</div>
            <div>Tipster</div>
            <div className="text-center">Pts</div>
            <div className="text-center">BP</div>
            <div className="text-center">W</div>
            <div className="text-center">Tips</div>
          </div>

          {/* Rows */}
          {loading ? (
            <div className="py-12 text-center">
              <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <Target className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>No tips submitted yet</p>
              <p className="text-sm mt-2">Be the first to make your predictions!</p>
            </div>
          ) : (
            leaderboard.map((entry, index) => {
              const bpNum = Math.round(((entry.total_points || 0) - (entry.correct_tips || 0)) * 100) / 100;
              const bp = bpNum.toFixed(2);
              const totalPts = Number(entry.total_points || 0).toFixed(2);
              return (
                <div 
                  key={entry.user_id} 
                  className={`leaderboard-row ${entry.user_id === user?.user_id ? 'bg-[#007AFF]/10' : ''}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`leaderboard-row-${entry.user_id}`}
                >
                  <div className="flex items-center justify-center">
                    {getRankIcon(entry.rank)}
                  </div>
                  <div className="font-medium truncate">
                    {entry.name}
                    {entry.user_id === user?.user_id && (
                      <span className="ml-2 text-[#007AFF] text-xs">(You)</span>
                    )}
                  </div>
                  <div className="text-center font-bold" style={{ color: sportColor }}>
                    {totalPts}
                  </div>
                  <div className={`text-center font-bold ${bpNum > 0 ? 'text-[#FFB81C]' : bpNum < 0 ? 'text-[#FF3B30]' : 'text-gray-400'}`} title="Bonus Points">
                    {bpNum > 0 ? `+${bp}` : bp}
                  </div>
                  <div className="text-center text-[#34C759]">
                    {entry.correct_tips}
                  </div>
                  <div className="text-center text-gray-500">
                    {entry.total_tips}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Back to Tipping */}
        <div className="mt-8 text-center">
          <Link to={`/tipping/${sport}`}>
            <Button className="bg-[#007AFF] hover:bg-[#3395FF]" data-testid="back-to-tipping-btn">
              <Target className="w-4 h-4 mr-2" />
              Back to Tipping
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
